import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * Databricks Node Schema
 * SQL Statement Execution API
 *
 * Operations:
 * - Execute Query: Run SQL statements
 * - Get Statement Status: Check async query status
 * - Cancel Statement: Cancel a running query
 */
const schema: NodePropertySchema = {
  nodeType: 'databricks',
  version: '1.0.0',

  properties: [
    // ==================================================================
    // CREDENTIAL SELECTOR
    // ==================================================================
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      required: true,
      description: 'Select the Databricks credential to use',
      credentialTypes: [{ name: 'databricks', required: true }],
    },

    // ==================================================================
    // OPERATION SELECTOR
    // ==================================================================
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      required: true,
      default: 'executeQuery',
      options: [
        { name: 'Execute Query', value: 'executeQuery', description: 'Execute a SQL statement' },
        { name: 'Get Statement Status', value: 'getStatementStatus', description: 'Get the status of a running statement' },
        { name: 'Cancel Statement', value: 'cancelStatement', description: 'Cancel a running statement' },
      ],
      description: 'The operation to perform',
    },

    // ==================================================================
    // WAREHOUSE ID (for executeQuery - optional if set in credential)
    // ==================================================================
    {
      key: 'warehouseId',
      label: 'SQL Warehouse ID',
      type: 'string',
      required: false,
      placeholder: '5c742f61f2594956',
      description: 'The SQL warehouse ID. Leave empty to use the default from your credential.',
      displayOptions: {
        show: {
          operation: ['executeQuery'],
        },
      },
    },

    // ==================================================================
    // SQL STATEMENT (for executeQuery)
    // ==================================================================
    {
      key: 'statement',
      label: 'SQL Statement',
      type: 'string',
      required: true,
      placeholder: 'SELECT * FROM my_catalog.my_schema.my_table LIMIT 100',
      description: 'The SQL statement to execute',
      displayOptions: {
        show: {
          operation: ['executeQuery'],
        },
      },
      typeOptions: {
        rows: 6,
      },
      validation: [{ type: 'required', message: 'SQL statement is required' }],
    },

    // ==================================================================
    // STATEMENT ID (for getStatementStatus and cancelStatement)
    // ==================================================================
    {
      key: 'statementId',
      label: 'Statement ID',
      type: 'string',
      required: true,
      placeholder: '01f1072e-bc79-1586-bb0c-5c7850e9f78a',
      description: 'The ID of the statement to check or cancel',
      displayOptions: {
        show: {
          operation: ['getStatementStatus', 'cancelStatement'],
        },
      },
      validation: [{ type: 'required', message: 'Statement ID is required' }],
    },

    // ==================================================================
    // EXECUTION OPTIONS (for executeQuery)
    // ==================================================================
    {
      key: 'waitTimeout',
      label: 'Wait Timeout',
      type: 'string',
      default: '30s',
      placeholder: '30s',
      description: 'Maximum time to wait for query completion. Use "0s" for async execution. Format: Ns, Nm, Nh (e.g., "30s", "5m")',
      displayOptions: {
        show: {
          operation: ['executeQuery'],
        },
      },
    },
    {
      key: 'rowLimit',
      label: 'Row Limit',
      type: 'number',
      default: 10000,
      description: 'Maximum number of rows to return in the result. Maximum allowed is 100000.',
      displayOptions: {
        show: {
          operation: ['executeQuery'],
        },
      },
      typeOptions: {
        minValue: 1,
        maxValue: 100000,
      },
    },
    {
      key: 'byteLimitMb',
      label: 'Byte Limit (MB)',
      type: 'number',
      default: 10,
      description: 'Maximum size of the result in megabytes. Maximum allowed is 100 MB.',
      displayOptions: {
        show: {
          operation: ['executeQuery'],
        },
      },
      typeOptions: {
        minValue: 1,
        maxValue: 100,
      },
    },

    // ==================================================================
    // PARAMETERS (for parameterized queries)
    // ==================================================================
    {
      key: 'parameters',
      label: 'Query Parameters',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: true,
      },
      default: {},
      placeholder: 'Add Parameter',
      description: 'Named parameters for parameterized SQL queries',
      displayOptions: {
        show: {
          operation: ['executeQuery'],
        },
      },
      fixedCollectionOptions: [
        {
          name: 'values',
          displayName: 'Parameter',
          values: [
            {
              key: 'name',
              label: 'Name',
              type: 'string',
              default: '',
              placeholder: 'param_name',
              required: true,
              description: 'Parameter name (use :param_name in your SQL)',
            },
            {
              key: 'value',
              label: 'Value',
              type: 'string',
              default: '',
              placeholder: 'parameter value',
              description: 'Parameter value',
            },
            {
              key: 'type',
              label: 'Type',
              type: 'options',
              default: 'STRING',
              options: [
                { name: 'String', value: 'STRING' },
                { name: 'Integer', value: 'INT' },
                { name: 'Long', value: 'LONG' },
                { name: 'Float', value: 'FLOAT' },
                { name: 'Double', value: 'DOUBLE' },
                { name: 'Boolean', value: 'BOOLEAN' },
                { name: 'Date', value: 'DATE' },
                { name: 'Timestamp', value: 'TIMESTAMP' },
              ],
              description: 'Data type of the parameter',
            },
          ],
        },
      ],
    },

    // ==================================================================
    // SETTINGS TAB
    // ==================================================================
    {
      key: 'catalog',
      label: 'Default Catalog',
      type: 'string',
      section: 'settings',
      default: '',
      placeholder: 'my_catalog',
      description: 'Default catalog for the query (optional)',
      displayOptions: {
        show: {
          operation: ['executeQuery'],
        },
      },
    },
    {
      key: 'schema',
      label: 'Default Schema',
      type: 'string',
      section: 'settings',
      default: '',
      placeholder: 'my_schema',
      description: 'Default schema for the query (optional)',
      displayOptions: {
        show: {
          operation: ['executeQuery'],
        },
      },
    },
    {
      key: 'disposition',
      label: 'Disposition',
      type: 'options',
      section: 'settings',
      default: 'INLINE',
      options: [
        { name: 'Inline', value: 'INLINE', description: 'Return data directly in the response' },
        { name: 'External Links', value: 'EXTERNAL_LINKS', description: 'Return links to data (for large results)' },
      ],
      description: 'How to return the query results',
      displayOptions: {
        show: {
          operation: ['executeQuery'],
        },
      },
    },
    {
      key: 'format',
      label: 'Result Format',
      type: 'options',
      section: 'settings',
      default: 'JSON_ARRAY',
      options: [
        { name: 'JSON Array', value: 'JSON_ARRAY', description: 'Each row as a JSON array' },
        { name: 'Arrow Stream (IPC)', value: 'ARROW_STREAM', description: 'Apache Arrow format for efficient data transfer' },
      ],
      description: 'Format of the result data',
      displayOptions: {
        show: {
          operation: ['executeQuery'],
        },
      },
    },
    {
      key: 'continueOnFail',
      label: 'Continue on Fail',
      type: 'boolean',
      section: 'settings',
      default: false,
      description: 'Continue workflow execution even if this operation fails',
    },
  ],
};

export default schema;
